// «Ponerse al día» (ios/Sources/Views/CatchUpView.swift, Android CatchUpScreen, Mac sidebar chip;
// PARITY P295): the unread conversations as a deck of cards, one at a time. Drag right (or «Marcar
// leído», →) marks it read, left (or «Después», ←) leaves it, a click (or Enter) opens it. The deck is
// a snapshot taken on open, so the card under the pointer never turns into another one. The chip
// leads the sidebar's shortcut row only while something is waiting (the Mac's rule).
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { data } from '../../../bridge/client';
import { queue } from '../../../core/catchUp';
import type { ChannelListSection, DisplayChannel } from '../../../core/channelListDisplayModels';
import type { CatchUpLine } from '../../../data/features/extras';
import { Icon } from '../../../design/icons';
import { t, tf } from '../../../i18n/i18n';
import { useLive } from '../../hooks';
import './extras.css';

export function CatchUpChip({ sections, onOpenChannel }: { sections: ChannelListSection[]; onOpenChannel(id: string): void }) {
  const waiting = useMemo(() => queue(sections), [sections]);
  const [deck, setDeck] = useState<DisplayChannel[] | null>(null);
  return (
    <>
      {waiting.length ? (
        <button className="chip catch-up-chip" title={t('catchUp.title')} aria-label={t('catchUp.title')} onClick={() => setDeck(waiting)}>
          <Icon name="stack" size={16} />
        </button>
      ) : null}
      {deck
        ? createPortal(
            <CatchUpDeck
              deck={deck}
              onOpen={(id) => {
                setDeck(null);
                onOpenChannel(id);
              }}
              onDismiss={() => setDeck(null)}
            />,
            document.body,
          )
        : null}
    </>
  );
}

function CatchUpDeck({ deck, onOpen, onDismiss }: { deck: DisplayChannel[]; onOpen(id: string): void; onDismiss(): void }) {
  const [index, setIndex] = useState(0);
  const row = index < deck.length ? deck[index] : null;
  const advance = (markRead: boolean) => {
    if (!row) return;
    if (markRead) void data.call('markChannelRead', row.id).catch(() => undefined);
    setIndex((i) => i + 1);
  };
  const latest = useRef({ advance, row, onOpen, onDismiss });
  latest.current = { advance, row, onOpen, onDismiss };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const l = latest.current;
      if (e.key === 'Escape') l.onDismiss();
      else if (!l.row) return;
      else if (e.key === 'ArrowRight') l.advance(true);
      else if (e.key === 'ArrowLeft') l.advance(false);
      else if (e.key === 'Enter') l.onOpen(l.row.id);
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="scrim catch-up-scrim" onMouseDown={(e) => e.target === e.currentTarget && onDismiss()}>
      <div className="catch-up" role="dialog" aria-modal="true" aria-label={t('catchUp.title')}>
        <header className="catch-up-head">
          <button type="button" className="icon-button" onClick={onDismiss} aria-label={t('common.close')} title={t('common.close')}>
            <Icon name="x" size={17} />
          </button>
          <h2>{t('catchUp.title')}</h2>
          <span className="catch-up-progress">{row ? tf('catchUp.progress', index + 1, deck.length) : ''}</span>
        </header>
        {row ? (
          <>
            <CatchUpCard key={row.id} row={row} onOpen={() => onOpen(row.id)} onSwiped={advance} />
            <div className="catch-up-actions">
              <button type="button" className="catch-up-later" onClick={() => advance(false)}>
                {t('catchUp.later')}
              </button>
              <button type="button" className="catch-up-read" onClick={() => advance(true)}>
                {t('catchUp.markRead')}
              </button>
            </div>
          </>
        ) : (
          <div className="catch-up-done">
            <span className="catch-up-done-mark">
              <Icon name="check" size={30} strokeWidth={2.4} />
            </span>
            <h3>{t('catchUp.done.title')}</h3>
            <p>{t('catchUp.done.description')}</p>
            <button type="button" className="catch-up-read" onClick={onDismiss}>
              {t('common.close')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function CatchUpCard({ row, onOpen, onSwiped }: { row: DisplayChannel; onOpen(): void; onSwiped(markRead: boolean): void }) {
  // The store first, then the conversation's newest page, then the store again (the Swift card's task).
  const { value: lines } = useLive<CatchUpLine[]>(() => data.call('catchUpCard', row.id), ['posts', 'users', 'channelMembers'], [row.id]);
  useEffect(() => {
    // Fetch the newest page WITHOUT viewing the channel: showing a card must not mark it read.
    void data.call('catchUpRefresh', row.id).catch(() => undefined);
  }, [row.id]);

  const card = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x: number; id: number; moved: boolean } | null>(null);
  const [offset, setOffset] = useState(0);
  const [leaving, setLeaving] = useState<0 | 1 | -1>(0);

  const width = () => card.current?.getBoundingClientRect().width ?? 400;
  return (
    <div
      ref={card}
      className={`catch-up-card${leaving ? ' leaving' : ''}`}
      style={{ transform: `translateX(${leaving ? leaving * width() * 1.4 : offset}px) rotate(${((leaving ? leaving * 1.4 : offset / width()) * 8).toFixed(2)}deg)` }}
      role="button"
      tabIndex={0}
      data-catch-up-card={row.id}
      onPointerDown={(e) => {
        drag.current = { x: e.clientX, id: e.pointerId, moved: false };
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      }}
      onPointerMove={(e) => {
        const d = drag.current;
        if (!d || d.id !== e.pointerId) return;
        const dx = e.clientX - d.x;
        if (Math.abs(dx) > 12) d.moved = true;
        if (d.moved) setOffset(dx);
      }}
      onPointerUp={(e) => {
        const d = drag.current;
        drag.current = null;
        if (!d) return;
        if (!d.moved) return onOpen();
        const dx = e.clientX - d.x;
        if (Math.abs(dx) > width() * 0.28) {
          const right = dx > 0;
          setLeaving(right ? 1 : -1);
          setTimeout(() => onSwiped(right), 180);
        } else setOffset(0);
      }}
      onPointerCancel={() => {
        drag.current = null;
        setOffset(0);
      }}
    >
      <div className="catch-up-card-head">
        <span className="catch-up-name">{row.name}</span>
        {row.mentionCount > 0 ? <span className="catch-up-mentions">@{row.mentionCount}</span> : null}
      </div>
      <div className="catch-up-lines">
        {lines && lines.length ? (
          lines.map((line) => (
            <div key={line.id} className="catch-up-line">
              <div className="catch-up-author">{line.author}</div>
              <div className="catch-up-text">{line.text}</div>
            </div>
          ))
        ) : (
          // Nothing cached yet: the row's own preview stands in while the page arrives.
          <div className="catch-up-text">{row.preview ?? ''}</div>
        )}
      </div>
      <div className="catch-up-hint">{t('catchUp.hint')}</div>
    </div>
  );
}
